import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus, Edit2, Trash2, Save, X, RefreshCw, Eye, EyeOff, 
  Sparkles, Calendar, Clock, Image, Play, Pause, Settings,
  FileText, Tag, ArrowLeft, Check, Upload
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = {
  'immobilienmarkt': { name: 'Immobilienmarkt', icon: '📊', color: 'bg-blue-500' },
  'tips-tricks': { name: 'Tipps und Tricks', icon: '💡', color: 'bg-green-500' },
  'bauprojekte': { name: 'Bauprojekte', icon: '🏗️', color: 'bg-orange-500' },
  'news-trends': { name: 'News & Trends', icon: '📰', color: 'bg-purple-500' }
};

const FREQUENCIES = {
  'daily': { name: 'Täglich', desc: 'Jeden Tag um 08:00 Uhr' },
  'weekly': { name: 'Wöchentlich', desc: 'Jeden Montag um 08:00 Uhr' },
  'monthly': { name: 'Monatlich', desc: 'Am 1. des Monats um 08:00 Uhr' }
};

export const BlogAdmin = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateConfig, setGenerateConfig] = useState({
    category: 'news-trends',
    custom_topic: '',
    generate_image: true,
    auto_publish: false
  });
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleConfig, setScheduleConfig] = useState({
    category: 'news-trends',
    frequency: 'weekly',
    generate_image: true,
    auto_publish: false
  });
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    meta_description: '',
    excerpt: '',
    content: '',
    category: 'news-trends',
    tags: [],
    status: 'draft'
  });

  useEffect(() => {
    fetchPosts();
    fetchSchedules();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/admin/posts`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedules = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/admin/schedules`);
      if (response.ok) {
        const data = await response.json();
        setSchedules(data);
      }
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  };

  const handleGeneratePost = async () => {
    setIsGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/blog/admin/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(generateConfig)
      });
      if (response.ok) {
        await fetchPosts();
        setShowGenerateModal(false);
        alert('Blog-Beitrag erfolgreich generiert!');
      } else {
        const error = await response.json();
        alert(`Fehler: ${error.detail}`);
      }
    } catch (error) {
      alert('Fehler beim Generieren: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCreateSchedule = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/admin/schedules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scheduleConfig)
      });
      if (response.ok) {
        await fetchSchedules();
        setShowScheduleModal(false);
        alert('Zeitplan erstellt!');
      } else {
        const error = await response.json();
        alert(`Fehler: ${error.detail}`);
      }
    } catch (error) {
      alert('Fehler: ' + error.message);
    }
  };

  const handleDeleteSchedule = async (id) => {
    if (!window.confirm('Zeitplan wirklich löschen?')) return;
    try {
      await fetch(`${API_URL}/api/blog/admin/schedules/${id}`, { method: 'DELETE' });
      await fetchSchedules();
    } catch (error) {
      alert('Fehler: ' + error.message);
    }
  };

  const handleToggleSchedule = async (id) => {
    try {
      await fetch(`${API_URL}/api/blog/admin/schedules/${id}/toggle`, { method: 'POST' });
      await fetchSchedules();
    } catch (error) {
      alert('Fehler: ' + error.message);
    }
  };

  const handleRunScheduleNow = async (id) => {
    if (!window.confirm('Jetzt einen Blog-Beitrag generieren?')) return;
    try {
      setIsGenerating(true);
      const response = await fetch(`${API_URL}/api/blog/admin/schedules/${id}/run-now`, {
        method: 'POST'
      });
      if (response.ok) {
        await fetchPosts();
        alert('Blog-Beitrag generiert!');
      }
    } catch (error) {
      alert('Fehler: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePublish = async (postId) => {
    try {
      await fetch(`${API_URL}/api/blog/admin/posts/${postId}/publish`, { method: 'POST' });
      await fetchPosts();
    } catch (error) {
      alert('Fehler: ' + error.message);
    }
  };

  const handleUnpublish = async (postId) => {
    try {
      await fetch(`${API_URL}/api/blog/admin/posts/${postId}/unpublish`, { method: 'POST' });
      await fetchPosts();
    } catch (error) {
      alert('Fehler: ' + error.message);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Blog-Beitrag wirklich löschen?')) return;
    try {
      await fetch(`${API_URL}/api/blog/admin/posts/${postId}`, { method: 'DELETE' });
      await fetchPosts();
    } catch (error) {
      alert('Fehler: ' + error.message);
    }
  };

  const handleRegenerateImage = async (postId) => {
    try {
      setIsGenerating(true);
      const response = await fetch(`${API_URL}/api/blog/admin/posts/${postId}/regenerate-image`, {
        method: 'POST'
      });
      if (response.ok) {
        await fetchPosts();
        alert('Bild neu generiert!');
      }
    } catch (error) {
      alert('Fehler: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleImageUpload = async (postId, file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setIsGenerating(true);
      const response = await fetch(`${API_URL}/api/blog/admin/posts/${postId}/upload-image`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        await fetchPosts();
        alert('Bild erfolgreich hochgeladen!');
      } else {
        const error = await response.json();
        alert(`Fehler: ${error.detail}`);
      }
    } catch (error) {
      alert('Fehler beim Hochladen: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const startEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      meta_description: post.meta_description || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      category: post.category,
      tags: post.tags || [],
      status: post.status
    });
  };

  const handleSavePost = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/admin/posts/${editingPost.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        await fetchPosts();
        setEditingPost(null);
      }
    } catch (error) {
      alert('Fehler: ' + error.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-[#264F77] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={onBack}
            className="inline-flex items-center text-gray-300 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Zurück zur Hauptkonsole
          </button>
          <h1 className="text-3xl font-bold">Blog-Verwaltung</h1>
          <p className="text-gray-300 mt-2">KI-gestützte Blog-Erstellung und Verwaltung</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1">
            {[
              { id: 'posts', label: 'Beiträge', icon: FileText },
              { id: 'generate', label: 'KI-Generator', icon: Sparkles },
              { id: 'schedule', label: 'Automatisierung', icon: Calendar }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'border-[#264F77] text-[#264F77]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Posts Tab */}
        {activeTab === 'posts' && (
          <>
            {/* Actions */}
            <div className="flex flex-wrap gap-4 mb-6">
              <button
                onClick={() => setShowGenerateModal(true)}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all"
              >
                <Sparkles size={20} className="mr-2" />
                Mit KI generieren
              </button>
              <button
                onClick={fetchPosts}
                className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <RefreshCw size={20} className="mr-2" />
                Aktualisieren
              </button>
            </div>

            {/* Edit Form */}
            {editingPost && (
              <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
                <h2 className="text-xl font-semibold text-[#264F77] mb-6">Beitrag bearbeiten</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Titel</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#264F77]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Slug (URL)</label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#264F77]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Kategorie</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#264F77]"
                    >
                      {Object.entries(CATEGORIES).map(([key, cat]) => (
                        <option key={key} value={key}>{cat.icon} {cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Meta-Beschreibung (SEO)</label>
                    <input
                      type="text"
                      value={formData.meta_description}
                      onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#264F77]"
                      maxLength={155}
                    />
                    <p className="text-xs text-gray-500 mt-1">{formData.meta_description.length}/155 Zeichen</p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Teaser</label>
                    <textarea
                      value={formData.excerpt}
                      onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                      rows={2}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#264F77]"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Inhalt (Markdown)</label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      rows={12}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#264F77] font-mono text-sm"
                    />
                  </div>
                </div>
                <div className="flex gap-4 mt-6">
                  <button
                    onClick={handleSavePost}
                    className="inline-flex items-center px-6 py-2 bg-[#264F77] text-white rounded-lg hover:bg-[#1e3d5c]"
                  >
                    <Save size={20} className="mr-2" />
                    Speichern
                  </button>
                  <button
                    onClick={() => setEditingPost(null)}
                    className="inline-flex items-center px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    <X size={20} className="mr-2" />
                    Abbrechen
                  </button>
                </div>
              </div>
            )}

            {/* Posts List */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-[#264F77]">
                  Blog-Beiträge ({posts.length})
                </h2>
              </div>
              
              {loading ? (
                <div className="p-8 text-center text-gray-500">Laden...</div>
              ) : posts.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Sparkles size={48} className="mx-auto mb-4 text-gray-300" />
                  <p>Noch keine Blog-Beiträge vorhanden.</p>
                  <p className="text-sm">Klicken Sie auf "Mit KI generieren" um den ersten Beitrag zu erstellen.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Titel</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategorie</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Erstellt</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {posts.map((post) => (
                        <tr key={post.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              {post.has_image && (
                                <span className="text-green-500" title="Hat Bild">
                                  <Image size={16} />
                                </span>
                              )}
                              <div>
                                <p className="font-medium text-gray-900 line-clamp-1">{post.title}</p>
                                {post.ai_generated && (
                                  <span className="text-xs text-purple-600 flex items-center gap-1">
                                    <Sparkles size={12} /> KI-generiert
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white ${CATEGORIES[post.category]?.color || 'bg-gray-500'}`}>
                              {CATEGORIES[post.category]?.icon} {CATEGORIES[post.category]?.name || post.category}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                              post.status === 'published' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {post.status === 'published' ? 'Veröffentlicht' : 'Entwurf'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {formatDate(post.created_at)}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              {post.status === 'draft' ? (
                                <button
                                  onClick={() => handlePublish(post.id)}
                                  className="p-2 text-green-600 hover:bg-green-100 rounded-lg"
                                  title="Veröffentlichen"
                                >
                                  <Eye size={18} />
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleUnpublish(post.id)}
                                  className="p-2 text-yellow-600 hover:bg-yellow-100 rounded-lg"
                                  title="Zurückziehen"
                                >
                                  <EyeOff size={18} />
                                </button>
                              )}
                              <button
                                onClick={() => startEdit(post)}
                                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                                title="Bearbeiten"
                              >
                                <Edit2 size={18} />
                              </button>
                              {/* Image Upload */}
                              <label className="p-2 text-green-600 hover:bg-green-100 rounded-lg cursor-pointer" title="Bild hochladen">
                                <Upload size={18} />
                                <input
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp,image/gif"
                                  className="hidden"
                                  onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                      handleImageUpload(post.id, e.target.files[0]);
                                      e.target.value = '';
                                    }
                                  }}
                                />
                              </label>
                              <button
                                onClick={() => handleRegenerateImage(post.id)}
                                disabled={isGenerating}
                                className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg disabled:opacity-50"
                                title="Bild mit KI generieren"
                              >
                                <Sparkles size={18} />
                              </button>
                              <button
                                onClick={() => handleDeletePost(post.id)}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                                title="Löschen"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Generate Tab */}
        {activeTab === 'generate' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="text-white" size={32} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">KI Blog-Generator</h2>
                <p className="text-gray-500 mt-2">Lassen Sie GPT-4 einen SEO-optimierten Blogbeitrag erstellen</p>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Kategorie wählen</label>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(CATEGORIES).map(([key, cat]) => (
                      <button
                        key={key}
                        onClick={() => setGenerateConfig({ ...generateConfig, category: key })}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          generateConfig.category === key
                            ? 'border-[#264F77] bg-[#264F77]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="text-2xl">{cat.icon}</span>
                        <p className="font-medium mt-2">{cat.name}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Spezifisches Thema (optional)
                  </label>
                  <input
                    type="text"
                    value={generateConfig.custom_topic}
                    onChange={(e) => setGenerateConfig({ ...generateConfig, custom_topic: e.target.value })}
                    placeholder="z.B. Tipps für den ersten Immobilienkauf"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#264F77]"
                  />
                </div>

                <div className="flex flex-col gap-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={generateConfig.generate_image}
                      onChange={(e) => setGenerateConfig({ ...generateConfig, generate_image: e.target.checked })}
                      className="w-5 h-5 text-[#264F77] border-gray-300 rounded"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-700">
                      Titelbild mit GPT Image 1 generieren
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={generateConfig.auto_publish}
                      onChange={(e) => setGenerateConfig({ ...generateConfig, auto_publish: e.target.checked })}
                      className="w-5 h-5 text-[#264F77] border-gray-300 rounded"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-700">
                      Automatisch veröffentlichen
                    </span>
                  </label>
                </div>

                <button
                  onClick={handleGeneratePost}
                  disabled={isGenerating}
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw size={20} className="animate-spin" />
                      Wird generiert... (kann bis zu 1 Minute dauern)
                    </>
                  ) : (
                    <>
                      <Sparkles size={20} />
                      Blog-Beitrag generieren
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Automatische Blog-Generierung</h2>
              <button
                onClick={() => setShowScheduleModal(true)}
                className="inline-flex items-center px-4 py-2 bg-[#264F77] text-white rounded-lg hover:bg-[#1e3d5c]"
              >
                <Plus size={20} className="mr-2" />
                Neuer Zeitplan
              </button>
            </div>

            {schedules.length === 0 ? (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Keine Zeitpläne vorhanden</h3>
                <p className="text-gray-500 mb-6">
                  Erstellen Sie einen Zeitplan, um automatisch Blog-Beiträge zu generieren.
                </p>
                <button
                  onClick={() => setShowScheduleModal(true)}
                  className="inline-flex items-center px-6 py-3 bg-[#264F77] text-white rounded-lg hover:bg-[#1e3d5c]"
                >
                  <Plus size={20} className="mr-2" />
                  Ersten Zeitplan erstellen
                </button>
              </div>
            ) : (
              <div className="grid gap-4">
                {schedules.map((schedule) => (
                  <div key={schedule.id} className={`bg-white rounded-xl shadow-lg p-6 ${!schedule.enabled ? 'opacity-60' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${CATEGORIES[schedule.category]?.color || 'bg-gray-500'}`}>
                          <span className="text-2xl">{CATEGORIES[schedule.category]?.icon}</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {CATEGORIES[schedule.category]?.name || schedule.category}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {FREQUENCIES[schedule.frequency]?.desc || schedule.frequency}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right text-sm">
                          <p className="text-gray-500">Letzte Ausführung</p>
                          <p className="font-medium">{formatDate(schedule.last_run) || 'Noch nie'}</p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleToggleSchedule(schedule.id)}
                            className={`p-2 rounded-lg ${schedule.enabled ? 'text-green-600 hover:bg-green-100' : 'text-gray-400 hover:bg-gray-100'}`}
                            title={schedule.enabled ? 'Deaktivieren' : 'Aktivieren'}
                          >
                            {schedule.enabled ? <Play size={20} /> : <Pause size={20} />}
                          </button>
                          <button
                            onClick={() => handleRunScheduleNow(schedule.id)}
                            disabled={isGenerating}
                            className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg disabled:opacity-50"
                            title="Jetzt ausführen"
                          >
                            <Sparkles size={20} />
                          </button>
                          <button
                            onClick={() => handleDeleteSchedule(schedule.id)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                            title="Löschen"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t flex gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Image size={14} />
                        Bild: {schedule.generate_image ? 'Ja' : 'Nein'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Check size={14} />
                        Auto-Publish: {schedule.auto_publish ? 'Ja' : 'Nein'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Blog-Beitrag generieren</h3>
            <p className="text-gray-500 mb-6">
              Wählen Sie eine Kategorie und optional ein spezifisches Thema.
            </p>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2">Kategorie</label>
                <select
                  value={generateConfig.category}
                  onChange={(e) => setGenerateConfig({ ...generateConfig, category: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  {Object.entries(CATEGORIES).map(([key, cat]) => (
                    <option key={key} value={key}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Thema (optional)</label>
                <input
                  type="text"
                  value={generateConfig.custom_topic}
                  onChange={(e) => setGenerateConfig({ ...generateConfig, custom_topic: e.target.value })}
                  placeholder="z.B. Energieeffizienz bei Neubauten"
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowGenerateModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Abbrechen
              </button>
              <button
                onClick={handleGeneratePost}
                disabled={isGenerating}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {isGenerating ? 'Generiert...' : 'Generieren'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Neuer Zeitplan</h3>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2">Kategorie</label>
                <select
                  value={scheduleConfig.category}
                  onChange={(e) => setScheduleConfig({ ...scheduleConfig, category: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  {Object.entries(CATEGORIES).map(([key, cat]) => (
                    <option key={key} value={key}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Häufigkeit</label>
                <select
                  value={scheduleConfig.frequency}
                  onChange={(e) => setScheduleConfig({ ...scheduleConfig, frequency: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                >
                  {Object.entries(FREQUENCIES).map(([key, freq]) => (
                    <option key={key} value={key}>{freq.name} - {freq.desc}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={scheduleConfig.generate_image}
                  onChange={(e) => setScheduleConfig({ ...scheduleConfig, generate_image: e.target.checked })}
                  className="w-5 h-5"
                />
                <span className="ml-3 text-sm">Titelbild generieren</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={scheduleConfig.auto_publish}
                  onChange={(e) => setScheduleConfig({ ...scheduleConfig, auto_publish: e.target.checked })}
                  className="w-5 h-5"
                />
                <span className="ml-3 text-sm">Automatisch veröffentlichen</span>
              </label>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowScheduleModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Abbrechen
              </button>
              <button
                onClick={handleCreateSchedule}
                className="flex-1 px-4 py-2 bg-[#264F77] text-white rounded-lg hover:bg-[#1e3d5c]"
              >
                Erstellen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isGenerating && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 text-center max-w-sm">
            <RefreshCw size={48} className="animate-spin text-purple-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">KI arbeitet...</h3>
            <p className="text-gray-500">Dies kann bis zu einer Minute dauern.</p>
          </div>
        </div>
      )}
    </div>
  );
};
